import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { Typography, Tooltip, Button, Box, Tabs, Tab } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { PassContext } from '../pass-context'
import { AppContext } from '/providers/app-context'
import { urlsMatchIgnoringCacheBust } from '/components/editor/image-url'
import ImageSelector from '/components/image-selector'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'

const variablesPaths = {
  "logo": "images.google.logo",
  "wideLogo": "images.google.wide_logo"
}

const PopupDialogLogoImage = ({ open, close, sources, logoType }) => {

  const { classData, objectData, variables, setClassData, setObjectData, setVariables, passType, openVariablesManager } = useContext(PassContext)
  const { imageList } = useContext(AppContext)

  const [newClassData, setNewClassData] = useState({})
  const [newObjectData, setNewObjectData] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [imageType, setImageType] = useState("logo")
  const [requiredSize, setRequiredSize] = useState("")

  useEffect(() => {
    if (open) {
      setNewClassData(_.cloneDeep(classData))
    }
  }, [open, classData])

  useEffect(() => {
    if (open) {
      setNewObjectData(_.cloneDeep(objectData))
    }
  }, [open, objectData])

  useEffect(() => {
    if (open) {
      setNewVariables(_.cloneDeep(variables))
    }
  }, [open, variables])

  useEffect(() => {
    if (logoType) setImageType(logoType)
  }, [logoType])

  const currentImageUrl = useMemo(() => {
    if (!open) return null

    const imageUrl = _.get(newVariables, variablesPaths[imageType]) || dataSelector({ objectData: newObjectData, variables: newVariables, path: sources[imageType].path }) || dataSelector({ objectData: newClassData, variables: newVariables, path: sources[imageType].path })
    const img = imageList.find(i => urlsMatchIgnoringCacheBust(i.s3_url, imageUrl))
    const url = img ? img.s3_url : imageUrl
    return url
  }, [open, sources, imageType, newClassData, newObjectData, newVariables, imageList])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateWideLogoSource = updater => {
    if (imageType !== "wideLogo" || !sources?.wideLogo?.path) return

    const wideLogoPath = _.toPath(sources.wideLogo.path).slice(0, -2)

    if (sources.source === "class") {
      updater(setNewClassData, newClassData, wideLogoPath)
    } else {
      updater(setNewObjectData, newObjectData, wideLogoPath)
    }
  }

  const removeWideLogoFromSource = () => {
    updateWideLogoSource((setData, data, wideLogoPath) => {
      const nextData = _.cloneDeep(data)
      _.unset(nextData, wideLogoPath)
      setData(nextData)
    })
  }

  const addWideLogoToSource = () => {
    updateWideLogoSource((setData, data, wideLogoPath) => {
      const nextData = _.cloneDeep(data)
      _.set(nextData, wideLogoPath, {
        sourceUri: {
          uri: "{{images.google.wide_logo}}"
        }
      })
      setData(nextData)
    })
  }

  const removeImageFromTemplate = () => {
    setNewVariables(prev => {
      let nd = { ...prev }
      _.unset(nd, variablesPaths[imageType])
      return nd
    })
    removeWideLogoFromSource()
  }

  const setImageUrl = imageUrl => {
    setNewVariables(prev => {
      let nd = { ...prev }
      _.set(nd, variablesPaths[imageType], imageUrl)
      return nd
    })
    addWideLogoToSource()
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {

    if (!_.isEqual(newClassData, classData)) setClassData(newClassData)
    if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)
    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose} width={1000} variant="solid">

        <Tabs value={imageType} onChange={(e, v) => setImageType(v)} aria-label="basic tabs example">
          <Tab label="Logo" value="logo" />
          <Tab label="Wide Logo" value="wideLogo" />
        </Tabs>


        {/* Header */}
        <div className="flex-shrink-0 p-2">
          <div className="flex justify-between items-center">
            <Typography color="secondary" variant="h6">{imageType} ({requiredSize})</Typography>

            <Tooltip title="Press ESC to dismiss">
              <Button endIcon={<EastRoundedIcon />} onClick={handleClose}>Cancel</Button>
            </Tooltip>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-5 p-2">
          <ImageSelector
            passType={passType}
            currentImageUrl={currentImageUrl}
            setImageUrl={setImageUrl}
            imageType={imageType}
            deleteAllowed={true}
            removeImageFromTemplate={removeImageFromTemplate}
            setRequiredSize={setRequiredSize}
          />
        </div>

        {/* Footer */}
        <Box className="flex-shrink-0 p-3">
          <hr className="my-2" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
            <Button variant="contained" onClick={saveFormData}>Save</Button>
          </div>
        </Box>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogLogoImage
